import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { UserPermission } from '@/constants/permissions'
import { usePermission } from '@/hooks/use-permission'
import { Info, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ContentSection } from '../components/content-section'
import { CreateRoleDialog } from './components/create-role-dialog'
import { DeleteRoleDialog } from './components/delete-role-dialog'
import { EditRoleDialog } from './components/edit-role-dialog'
import { PermissionsMatrix } from './components/permissions-matrix'
import { usePermissions, useRoles } from './hooks/use-roles'
import type { Role } from './types'

export function SettingsRolesPermissions() {
  const { t } = useTranslation()
  const { can } = usePermission()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)
  const [deletingRole, setDeletingRole] = useState<Role | null>(null)
  const { data: roles = [], isLoading: isLoadingRoles } = useRoles()
  const { data: allPermissions = [], isLoading: isLoadingPermissions } =
    usePermissions()

  const isLoading = isLoadingRoles || isLoadingPermissions
  const canCreateRole = can(UserPermission.ADD_ROLE)
  const canUpdateRole = can(UserPermission.CHANGE_ROLE)
  const canDeleteRole = can(UserPermission.DELETE_ROLE)

  return (
    <>
      <ContentSection
        title={t('roles.title')}
        desc={t('roles.description')}
        fullWidth={true}
      >
        <div className='space-y-6'>
          <Alert>
            <Info className='h-4 w-4' />
            <AlertDescription>{t('roles.info')}</AlertDescription>
          </Alert>

          {canCreateRole && (
            <div className='flex items-center justify-end'>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className='me-2 h-4 w-4' />
                {t('roles.createRole', 'Create Role')}
              </Button>
            </div>
          )}

          <div>
            <PermissionsMatrix
              roles={roles}
              allPermissions={allPermissions}
              isLoading={isLoading}
              onEditRole={canUpdateRole ? setEditingRole : undefined}
              onDeleteRole={canDeleteRole ? setDeletingRole : undefined}
              canUpdatePermissions={canUpdateRole}
            />
          </div>
        </div>
      </ContentSection>

      <CreateRoleDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <EditRoleDialog
        role={editingRole}
        open={!!editingRole}
        onOpenChange={(open) => !open && setEditingRole(null)}
      />

      <DeleteRoleDialog
        role={deletingRole}
        open={!!deletingRole}
        onOpenChange={(open) => !open && setDeletingRole(null)}
      />
    </>
  )
}
